import styled from 'styled-components';
import { cssVars } from '@/styles/Themes/cssVars';
import Screen from '@/styles/Themes/Screen';
import Image from 'next/image';
import Text from 'src/styles/Themes/Text';

export const MainWrapper = styled.div`
    background: ${cssVars.bg_lightBlue_darker};
    padding: 32px;
    position: relative;
`;

export const ContentWrapper = styled.div`
    max-width: 1200px;
    margin: 0 auto;
`;

export const Breadcrumb = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 2rem;
    color: #666;
`;

export const BreadcrumbLink = styled.a`
    color: inherit;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

export const BreadcrumbSeparator = styled.span`
    color: #03d9b1;
`;

export const MainContent = styled.div`
    display: flex;
    flex-direction: column;

    ${Screen.desktop} {
        gap: 2rem;
        display: grid;
        grid-template-columns: 1fr 400px;
        align-items: start;
    }
`;

export const AuthorInfo = styled.div`
    z-index: 1;
    text-align: center;

    h1 {
        ${Text.Full.heading1}
        margin: 0;
        margin-bottom: 0.5rem;
        color: ${cssVars.fg_gray1_white};
    }

    h2 {
        ${Text.Full.heading2}
        color: ${cssVars.fg_gray1_white};
        margin-bottom: 1.5rem;
    }

    ${Screen.desktop} {
        text-align: left;
    }
`;

export const Gradient = styled.div`
    background-image: ${cssVars.gradient_author};
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    top: 25%;
    opacity: 0.6;
`;

export const Bio = styled.p`
    color: ${cssVars.fg_gray2_gray3};
    line-height: 1.6;
    margin-bottom: 2rem;

    a {
        color: ${cssVars.fg_green_greenLight};
        text-decoration: underline;
        font-weight: 500;
    }

    div {
        ${Text.Full.medium.regular};
    }
    ul {
        padding-left: 10px;
        margin: 0;
    }

    li::marker {
        content: '- ';
    }
`;

export const MainImage = styled(Image)`
    display: block;
    width: 100%;
    height: auto;
    object-fit: cover;
    object-position: top;
`;

export const Education = styled.div`
    display: flex;
    background: ${cssVars.bg_white_darker_07};
    border-radius: 15px;
    box-shadow: 0px 4px 23px ${cssVars.shadow_normal};
    margin-bottom: 2rem;
    color: ${cssVars.fg_gray2_white};
    font-size: 12px;
    padding: 4px 20px;
    text-align: left;

    label {
        font-weight: 500;
        margin-right: 8px;
    }
    ${Screen.desktop} {
        font-size: 14px;
        padding: 12px 20px;
    }
`;

export const SocialLinks = styled.div`
    display: flex;
    justify-content: center;
    gap: 14px;

    ${Screen.desktop} {
        justify-content: start;
    }

    a {
        display: block;
        transition: transform 0.2s;

        &:hover {
            transform: translateY(-2px);
        }
    }
`;

export const ProfileImage = styled.div`
    z-index: 1;
    border-radius: 18px;
    overflow: hidden;
    border: 2px solid #03d9b1;
    background-color: ${cssVars.bg_white_blue};

    span {
        display: block !important;
    }
    image {
        display: block;
    }

    ${Screen.ipad} {
        width: 400px;
        margin: 0 auto;
    }
`;

export const AboutSection = styled.section`
    display: grid;
    gap: 2rem;
    padding: 2rem 0;
    margin: 24px 0;

    ${Screen.desktop} {
        grid-template-columns: 2fr 1fr;
        align-items: start;
        margin: 64px 0;
    }
`;

export const Title = styled.h2`
    ${Text.Full.heading2}
    margin: 0;
    color: ${cssVars.fg_gray1_white};
    grid-column: 1 / -1;
`;

export const VideoContainer = styled.div`
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
    background: #f5f5f5;
    border-radius: 8px;
    overflow: hidden;

    height: 100%;
`;

export const Video = styled.iframe`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
`;

export const ContentCard = styled.div`
    background: ${cssVars.bg_white_dark3};
    color: ${cssVars.fg_gray2_gray3};
    padding: 26px 26px 44px 32px;
    height: 100%;
    border-radius: 1rem;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
`;

export const GallerySection = styled.section`
    background: ${cssVars.bg_blue_dark3};
    padding: 20px 16px 40px;
    width: 100%;
    border-radius: 16px;

    ${Screen.desktop} {
        padding: 20px 0 60px;
    }
`;

export const GalleryContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
`;

export const VideoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    width: 100%;
`;

export const VideoWrapper = styled.div`
    position: relative;
    padding-top: 56.25%; /* 16:9 Aspect Ratio */
    width: 100%;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    overflow: hidden;
`;

export const VideoTitle = styled.h3`
    color: white;
    margin-bottom: 2rem;
    ${Text.Full.heading2}
`;

export const BlogWrapper = styled.div`
    background-color: ${cssVars.bg_white_blue};
    width: 100%;
    padding: 22px 0 0;

    ${Screen.ipad} {
        padding: 27px 0 0;
    }

    ${Screen.desktop} {
        padding: 60px 0 0;
    }
`;

export const CardsContainer = styled.div`
    margin: 0 18px;
    max-width: 1124px;

    ${Screen.ipad} {
        margin: 0 30px;
    }
    ${Screen.desktop} {
        margin: 0 auto;
        padding: 0 30px;
    }
`;

export const BlogTitle = styled.h2`
    color: ${cssVars.fg_gray1_white};
    margin: 0;
    ${Text.Mobile.heading2};

    ${Screen.ipad} {
        ${Text.Ipad.heading3}
    }
    ${Screen.desktop} {
        ${Text.Desktop.heading2}
    }
`;

export const BlogContent = styled.div`
    display: grid;
    margin: 24px 0;
    grid-template: 1fr / none;
    grid-gap: 12px;
    *:nth-of-type(n + 3) {
        display: none;
    }
    ${Screen.ipad} {
        grid-gap: 24px;
    }
    ${Screen.desktop} {
        *:nth-of-type(n + 3) {
            display: block;
        }
        grid-template: 1fr / 1fr 1fr;
    }
`;
